
import { defineComponent, onMounted, onBeforeUnmount } from "vue"
import * as echarts from "echarts"

export default defineComponent({
  setup() {
    let scatterData: (string | number)[][] = []
    let useLogR = false
    let useLogG = false
    let useLogB = false
    let markerSize = 2
    let polygonData: number[][] = []

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    let scatterPlot

    let selectedPointsIndex: number[] = []

    /**
     * 获取散点图log的数据
     * @param data 原始数据
     * @param flagR R通道的基因强度是否需要log
     * @param flagG G通道的基因强度是否需要log
     * @param flagB B通道的基因强度是否需要log
     */
    const retrieveLogData = (data: (string | number)[][], flagR: boolean, flagG: boolean, flagB: boolean) => {
      let result = []
      let dataRMin = Infinity
      let dataRMax = -Infinity
      let dataGMin = Infinity
      let dataGMax = -Infinity
      let dataBMin = Infinity
      let dataBMax = -Infinity
      console.log("retrieveLogData", data[1])
      result.push(data[0])
      for (let i = 1; i < data.length; i++) {
        dataRMin = dataRMin < Number(data[i][2]) ? dataRMin : Number(data[i][2])
        dataRMax = dataRMax > Number(data[i][2]) ? dataRMax : Number(data[i][2])
        dataGMin = dataGMin < Number(data[i][3]) ? dataGMin : Number(data[i][3])
        dataGMax = dataGMax > Number(data[i][3]) ? dataGMax : Number(data[i][3])
        dataBMin = dataBMin < Number(data[i][4]) ? dataBMin : Number(data[i][4])
        dataBMax = dataBMax > Number(data[i][4]) ? dataBMax : Number(data[i][4])
      }
      for (let i = 1; i<data.length; i++) {
        result.push([data[i][0], data[i][1],
          dataRMin === dataRMax ? 0 : flagR ?
              (Math.log(Number(data[i][2]) + 1) - Math.log(dataRMin + 1)) / (Math.log(dataRMax + 1) - Math.log(dataRMin + 1)) * 255 :
              (Number(data[i][2]) - dataRMin) / (dataRMax - dataRMin) * 255,
          dataGMin === dataGMax ? 0 : flagG ?
              (Math.log(Number(data[i][3]) + 1) - Math.log(dataGMin + 1)) / (Math.log(dataGMax + 1) - Math.log(dataGMin + 1)) * 255 :
              (Number(data[i][3]) - dataGMin) / (dataGMax - dataGMin) * 255,
          dataBMin === dataBMax ? 0 : flagB ?
              (Math.log(Number(data[i][4]) + 1) - Math.log(dataBMin + 1)) / (Math.log(dataBMax + 1) - Math.log(dataBMin + 1)) * 255 :
              (Number(data[i][4]) - dataBMin) / (dataBMax - dataBMin) * 255,
          data[i][5]
        ])
      }
      return result
    }

    /**
     * 获取散点图的选项设置
     */
    const getOption = () => {
      return {
        animation: false,
        toolbox: {
          show: true,
          feature: {
            saveAsImage: {
              title: 'save as image',
              type: 'png'
            },
            dataZoom: {
              title : {zoom : "zoom", back: "back"},
              show: true
            },
            myBrushPolygonSelect: {
              show: true,
              title: 'PolygonSelect, double click to end selection',
              icon: 'path://M0 100 L125 0 L300 0 L330 200 L100 200 Z',
              onclick: () => {
                console.log('PolygonSelect')
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                scatterPlot?.getZr()?.on('click', (event) => {
                  polygonData.push([event.event.zrX, event.event.zrY])
                  console.log("PolygonSelect points", polygonData)
                  if (polygonData.length > 1) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    scatterPlot?.dispatchAction({
                      type: 'brush',
                      areas: [
                        {
                          // 指定选框的类型。可以为 'polygon', 'rect', 'lineX', 'lineY'
                          brushType: 'polygon',
                          // 如果是“全局选框”，则使用 range 来描述选框的范围（里面是像素坐标）。
                          range: polygonData.slice(1)
                        }
                      ]
                    })
                  }
                })
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                scatterPlot?.getZr()?.on('dblclick', (event) => {
                  console.log('dblclick')
                  polygonData = []
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  scatterPlot?.getZr()?.off('click')
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  scatterPlot?.getZr()?.off('dblclick')
                })
              }
            },
            brush : {
              type: ["rect", "polygon", "keep", 'clear'],
              title : {
                rect: "rect",
                polygon: "polygon",
                keep : "keep",
                clear: "clear"
              }
            },
          }
        },
        dataset: {
          dimensions: ['x', 'y', 'value_r', 'value_g', 'value_b', 'idx'],
          source: retrieveLogData(scatterData, useLogR, useLogG, useLogB)
        },
        grid: {
          top: '5%',
          left: 'center',
          width: 'auto',
          height: '80%'
        },
        title: {
          text: 'SOView',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'cross'
          },
          formatter: (params: any) => {
            const data = params.data
            return 'x: ' + Number(data[0]).toFixed(2) + ' <br> y: '
                + Number(data[1]).toFixed(2) + '<br> value_r: '
                + Number(data[2]).toFixed(2) + '<br> value_g: '
                + Number(data[3]).toFixed(2) + '<br> value_b: '
                + Number(data[4]).toFixed(2) + '<br> index: ' + Number(data[5]).toFixed(2)
          }
        },
        xAxis: {
          show: false,
        },
        yAxis: {
          show: false,
        },
        brush: {
          inBrush: {
            opacity: 1
          },
          throttleType: 'debounce',
          throttleDelay: 1000,
        },
        series: {
          type: 'scatter',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: retrieveLogData(scatterData, useLogR, useLogG, useLogB),
          symbolSize: markerSize,
          itemStyle: {
            color: (params: any) => {
              // console.log(params)
              let color = [params.data[2], params.data[3], params.data[4]]
              // console.log(color)
              // console.log(`rgb(${color})`)
              return `rgb(${color})`
            }
          }
        },
      }
    }

    /**
     * 更新散点图
     */
    const updatePlot = () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      scatterPlot?.setOption(getOption(), true)
    }

    /**
     * 设置散点图的数据
     * @param data
     */
    const setPlotData = (data: (string | number)[][]) => {
      scatterData = data
      updatePlot()
    }

    /**
     * 设置某一通道的数据是否需要log
     * @param flag 是否要log
     * @param flagType R或者G或者B
     */
    const setLog = (flag: boolean, flagType: string) => {
      if (flagType === 'R') {
        useLogR = flag
      } else if (flagType === 'G') {
        useLogG = flag
      } else if (flagType === 'B') {
        useLogB = flag
      }
      updatePlot()
    }

    /**
     * 改变散点图的点大小
     * @param size
     */
    const changeMarkerSize = (size: number) => {
      markerSize = size
      console.log('marker size: ', markerSize)
      updatePlot()
    }
    /**
     * 区域选择的回调函数，用于更新直方图
     * @param params 含有选择索引的一个参数
     */
    const renderBrushed = (params: any) => {
      console.log('renderBrushed')
      let selectedIndex = params.batch[0].selected[0].dataIndex
      console.log(selectedIndex)
      let selectedData = []
      let valueData: number[] = []
      if (selectedIndex.length > 0){
        for (let index of selectedIndex) {
          selectedData.push(scatterData[index])
          valueData.push(Number(scatterData[index][2]))
        }
        console.log(selectedData)
        selectedPointsIndex = selectedIndex
      }
    }

    /**
     * 方便父组件获取当前选中的点
     */
    const getSelectedPointsIndex = () => {
      return selectedPointsIndex
    }

    /**
     * 当窗口变化时用来调整图的大小
     */
    const resizePlot = () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      scatterPlot?.resize()
    }

    onMounted(() => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      scatterPlot = echarts.init(document.getElementById('geneColorViewPlot'))
      scatterPlot?.on('brushselected', renderBrushed)
      window.addEventListener('resize', resizePlot)
    })

    onBeforeUnmount(() => {
      console.log('color onBeforeUnmount')
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      scatterPlot?.off('brushselected')
      window.removeEventListener('resize', resizePlot)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      scatterPlot.dispose()
    })

    return {
      setPlotData,
      setLog,
      changeMarkerSize,
      getSelectedPointsIndex
    }
  }
})

